import { HugeiconsIcon } from "@hugeicons/react";
import { Tick02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Button } from "@workspace/frontend-ui";
import type { FC } from "react";
import useRewardCenter from "../../actors/useRewardCenter";
import useTranslations from "../../i18n/useTranslations";
import type { Reward } from "../../types";
import Pill from "../Pill";
import RewardPayload from "./RewardPayload";

type Props = {
  reward: Reward;
};

const BUTTON = "h-8 rounded-full px-3 font-semibold text-sm";

const RewardAction: FC<Props> = ({ reward }) => {
  const { send } = useRewardCenter();
  const t = useTranslations();
  const { id, state, shape } = reward;
  const handleClaim = () => send("rewardCenter", { type: "claimReward", id });
  const handleChoose = () =>
    send("rewardCenter", { type: "toggleRewardOptions", id });

  if (state === "locked") {
    return (
      <Pill size="lg">
        {t("reward_center.reward.unlocks_at", { time: reward.unlocksAt })}
      </Pill>
    );
  }
  if (state === "cooldown") {
    return <Pill size="lg">{reward.countdown}</Pill>;
  }
  if (state === "confirmed" || state === "claimed") {
    return (
      <Pill size="lg" className="text-primary">
        <HugeiconsIcon icon={Tick02Icon} />
        {reward.amount ?? t("reward_center.reward.claimed")}
      </Pill>
    );
  }
  if (state === "error") {
    return (
      <Button variant="secondary" className={BUTTON} onClick={handleClaim}>
        {t("reward_center.reward.retry")}
      </Button>
    );
  }
  if ((shape === "choice" || shape === "bundles") && !reward.chosenOptionId) {
    return (
      <Button
        variant={state === "open" ? "secondary" : "default"}
        className={BUTTON}
        onClick={handleChoose}
      >
        {t("reward_center.reward.choose")}
      </Button>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <RewardPayload reward={reward} />
      <Button
        className={BUTTON}
        loading={state === "claiming"}
        disabled={state === "zero"}
        onClick={handleClaim}
      >
        {t("reward_center.reward.claim")}
      </Button>
    </span>
  );
};

export default RewardAction;
